/**
 * Assign Group Validation Utilities
 *
 * Provides validation functions for the Assign Group modal
 * All validations return errors that block the assignment
 */

import type { SelectedTank } from '@/types/view/home.view'
import type { GroupDetail, School } from '@/types/domain'

// ============================================
// Types
// ============================================

export type ValidationErrorType = 'ALREADY_ASSIGNED' | 'DATE_MISMATCH' | 'DATE_OVERLAP' | 'GROUP_NOT_SELECTED'

export interface ValidationError {
  type: ValidationErrorType
  tankId: string
  tankName: string
  message: string
  details?: {
    existingGroupName?: string
    conflictingSchoolName?: string
    conflictingTankName?: string
  }
}

export interface ValidationResult {
  isValid: boolean
  errors: ValidationError[]
}

// ============================================
// Validation Functions
// ============================================

/**
 * Validation 1: Check if school is already assigned to another group
 *
 * @param selectedTanks - Selected tanks
 * @param groupsWithDetails - All group details
 * @returns Array of errors for already assigned schools
 */
export function validateNotAlreadyAssigned(
  selectedTanks: SelectedTank[],
  groupsWithDetails: GroupDetail[]
): ValidationError[] {
  const errors: ValidationError[] = []

  for (const tank of selectedTanks) {
    if (!tank.schoolId) continue

    // Check if the schoolId exists in any group
    for (const group of groupsWithDetails) {
      const existingItem = group.items?.find((item) => item.school.id === tank.schoolId)

      if (existingItem) {
        errors.push({
          type: 'ALREADY_ASSIGNED',
          tankId: tank.tankId,
          tankName: tank.tankName,
          message: `${tank.tankName} is already assigned to group '${group.name}'.`,
          details: {
            existingGroupName: group.name,
          },
        })
        break // Stop searching once found in a group
      }
    }
  }

  return errors
}

/**
 * Validation 2: Check if school dates are valid
 *
 * @param selectedTanks - Selected tanks
 * @returns Array of date validity errors
 */
export function validateSchoolDates(selectedTanks: SelectedTank[]): ValidationError[] {
  const errors: ValidationError[] = []

  for (const tank of selectedTanks) {
    // Tank without schoolStartDate means empty tank (no school)
    if (!tank.schoolStartDate) {
      errors.push({
        type: 'DATE_MISMATCH',
        tankId: tank.tankId,
        tankName: tank.tankName,
        message: `${tank.tankName} does not have valid school information.`,
      })
      continue
    }

    // Check if endDate exists and is before startDate
    if (tank.schoolEndDate && tank.schoolStartDate > tank.schoolEndDate) {
      errors.push({
        type: 'DATE_MISMATCH',
        tankId: tank.tankId,
        tankName: tank.tankName,
        message: `${tank.tankName} has a school end date before the start date.`,
      })
    }
  }

  return errors
}

/**
 * Check if two date ranges overlap
 */
export function dateRangesOverlap(
  range1: { startDate: Date; endDate?: Date | null },
  range2: { startDate: Date; endDate?: Date | null }
): boolean {
  const start1 = range1.startDate.getTime()
  const end1 = range1.endDate?.getTime() ?? Infinity
  const start2 = range2.startDate.getTime()
  const end2 = range2.endDate?.getTime() ?? Infinity

  // Ranges don't overlap if: range1 ends before range2 starts OR range2 ends before range1 starts
  // Overlap is the opposite
  return !(end1 < start2 || end2 < start1)
}

/**
 * Validation 3: Check date overlap between selected schools and target group's existing schools
 *
 * @param selectedTanks - Selected tanks
 * @param targetGroupSchools - Existing schools in the target group
 * @returns Array of date overlap errors
 */
export function validateNoDateOverlap(
  selectedTanks: SelectedTank[],
  targetGroupSchools: School[]
): ValidationError[] {
  const errors: ValidationError[] = []

  for (const tank of selectedTanks) {
    if (!tank.schoolStartDate) continue

    const newSchoolRange = {
      startDate: tank.schoolStartDate,
      endDate: tank.schoolEndDate,
    }

    // Compare with existing schools in the target group
    for (const existingSchool of targetGroupSchools) {
      const existingRange = {
        startDate: existingSchool.startDate,
        endDate: existingSchool.endDate,
      }

      if (dateRangesOverlap(newSchoolRange, existingRange)) {
        errors.push({
          type: 'DATE_OVERLAP',
          tankId: tank.tankId,
          tankName: tank.tankName,
          message: `${tank.tankName}'s school dates overlap with '${existingSchool.name}'.`,
          details: {
            conflictingSchoolName: existingSchool.name,
          },
        })
        // Add error and move to next tank if any overlap found
        break
      }
    }
  }

  return errors
}

/**
 * Check date overlap between selected tanks
 *
 * @param selectedTanks - Selected tanks
 * @returns Array of date overlap errors between selected tanks
 */
export function validateNoDateOverlapBetweenSelected(
  selectedTanks: SelectedTank[]
): ValidationError[] {
  const errors: ValidationError[] = []
  const tanksWithDates = selectedTanks.filter((t) => t.schoolStartDate)

  for (let i = 0; i < tanksWithDates.length; i++) {
    for (let j = i + 1; j < tanksWithDates.length; j++) {
      const tank1 = tanksWithDates[i]
      const tank2 = tanksWithDates[j]

      const range1 = {
        startDate: tank1.schoolStartDate!,
        endDate: tank1.schoolEndDate,
      }
      const range2 = {
        startDate: tank2.schoolStartDate!,
        endDate: tank2.schoolEndDate,
      }

      if (dateRangesOverlap(range1, range2)) {
        errors.push({
          type: 'DATE_OVERLAP',
          tankId: tank1.tankId,
          tankName: tank1.tankName,
          message: `School dates of ${tank1.tankName} and ${tank2.tankName} overlap.`,
          details: {
            conflictingTankName: tank2.tankName,
          },
        })
      }
    }
  }

  return errors
}

// ============================================
// Main Validation Function
// ============================================

/**
 * Execute all validations
 *
 * @param selectedTanks - Selected tanks
 * @param targetGroupId - Target group ID
 * @param groupsWithDetails - All group details
 * @returns Complete validation result
 */
export function validateAssignment(
  selectedTanks: SelectedTank[],
  targetGroupId: string | null,
  groupsWithDetails: GroupDetail[]
): ValidationResult {
  const errors: ValidationError[] = []

  // 1. Check if school is already assigned to another group
  errors.push(...validateNotAlreadyAssigned(selectedTanks, groupsWithDetails))

  // 2. Check school date validity
  errors.push(...validateSchoolDates(selectedTanks))

  // 3. Check date overlap between selected tanks
  errors.push(...validateNoDateOverlapBetweenSelected(selectedTanks))

  // 4. If target group is selected, check date overlap with existing group schools
  if (targetGroupId) {
    const targetGroup = groupsWithDetails.find((g) => g.id === targetGroupId)
    if (targetGroup?.items) {
      const targetGroupSchools = targetGroup.items.map((item) => item.school)
      errors.push(...validateNoDateOverlap(selectedTanks, targetGroupSchools))
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Format validation errors by type
 */
export function formatValidationErrors(errors: ValidationError[]): string {
  if (errors.length === 0) return ''

  return errors.map((error) => `• ${error.message}`).join('\n')
}

/**
 * Filter errors for a specific tankId
 */
export function getErrorsForTank(errors: ValidationError[], tankId: string): ValidationError[] {
  return errors.filter((error) => error.tankId === tankId)
}

/**
 * Filter errors by type
 */
export function getErrorsByType(
  errors: ValidationError[],
  type: ValidationErrorType
): ValidationError[] {
  return errors.filter((error) => error.type === type)
}
